import { useInfiniteQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import type { ParsedUrlQuery } from "querystring";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { InstantSearchServerState } from "react-instantsearch";

import HighlightedText from "~/components/common/highlighted-text";
import { useCategory } from "~/contexts/category";
import { useConfiguration } from "~/contexts/configuration";
import { useServerSideProps } from "~/contexts/server-side-props";
import Editorials from "~/types/editorials";
import type { PaginationMeta } from "~/types/pagination";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import evaUtils from "~/utils/eva-utils";
import { extractFilters } from "~/utils/seo-utils";

import BreakpointAwareAsset from "../common/breakpoint-aware-asset";
import Container from "../common/container";
import EscapeNewLine from "../common/escape-new-line";
import Grid from "../common/grid";
import EditorialEntry from "../editorials/editorial-entry";
import EditorialLink from "../editorials/editorial-link/editorial-link";
import Breadcrumbs, { Breadcrumb } from "../product/product-breadcrumbs";
import ProductNavigationItemsCurrentPath from "../product/product-navigation-items-current-path";
import EvaProductListing from "./eva-product-listing";
import { Pagination } from "./pagination";
import styles from "./product-grid.module.scss";
import ProductGridCustomBackground from "./product-grid-custom-background";
import ProductGridSkeleton from "./product-grid-skeleton";

export function NumberProducts({ total, type }: { total: number; type: "mobile" | "desktop" }) {
  const t = useTranslations();

  return (
    <>
      {total > 0 ? (
        <div className={classNames(styles.prodCount, type === "mobile" ? styles.mobileOnly : styles.desktopOnly)}>
          <p>{t("generic.n_products", { number: total })}</p>
        </div>
      ) : (
        <div className={classNames(styles.noResults, type === "mobile" ? styles.mobileOnly : styles.desktopOnly)}>
          <h2>{t("generic.noresults")}</h2>
          <p dangerouslySetInnerHTML={{ __html: t("generic.noresultsparagraph") }} />
        </div>
      )}
    </>
  );
}

type Props = {
  breadcrumbs: Breadcrumb[];
  categoryLinks: Breadcrumb[];
  components?: Entry<Editorials.GridElement>[];
  componentsAfterListing?: Entry<unknown>[];
  navigation: Entry<unknown>;
  changeHeaderLayout?: boolean;
  backgroundColors?: string[];
  plpCustomHeader?: Editorials.ProductListingPage["plpCustomHeader"];
  setId?: string;
  listScope: string;
  initialData?: string | null;
  serverState?: InstantSearchServerState | null;
  serverUrl: string | null;
  algoliaUserToken: string;
  products?: any[];
  pagination?: PaginationMeta;
};

function getFilterSignature(query: ParsedUrlQuery): string {
  const filters = extractFilters(query);
  return Object.keys(filters)
    .sort()
    .map((key) => `${key}=${JSON.stringify(filters[key])}`)
    .join("&");
}

export default function ProductGrid(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const { algoliaIndexId } = useConfiguration();
  const { eva } = useConfiguration();
  const { sort, category } = useCategory();
  const { serverSideProps } = useServerSideProps();
  const componentsAfterListing = props.componentsAfterListing?.filter(isActiveEntry);

  // Pagination mode: starts from URL (?page=N), can be exited client-side when filters change
  const [isPaginationMode, setIsPaginationMode] = useState(!!router.query.page);

  // When user changes filters while in pagination mode, exit to infinite scroll.
  // We compare a filter signature (allowlist only) instead of the whole asPath so that
  // tracking params appended to the URL (e.g. Didomi's `_gl`) don't get mistaken for a
  // filter change, which would wrongly drop the page param and start infinite scroll
  // from the middle of the catalog.
  const initialFilterSignature = useRef(getFilterSignature(router.query));
  useEffect(() => {
    if (!isPaginationMode) return;

    // Only a real filter change (refinementList/range/sortBy/ratingMenu) exits pagination mode
    if (getFilterSignature(router.query) !== initialFilterSignature.current) {
      setIsPaginationMode(false);
      // Clean up URL: remove page param with shallow replace (no SSR reload)
      const { page, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
  }, [router.asPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useInfiniteQuery({
    queryKey: ["productListing", category.fields.categoryId, props.setId, sort],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) => {
      if (props.setId) {
        return evaUtils.getProductsBySetWithChildren(eva, props.setId, sort, pageParam);
      } else {
        return evaUtils.getProductsByCategoryWithChildren(eva, category.fields.categoryId, sort, pageParam);
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.Total! > lastPage.Page! * lastPage.PageSize!) {
        return lastPage.Page! + 1;
      }

      return undefined;
    },
    enabled: !props.serverState && (!!category.fields.categoryId || props.setId !== undefined),
    initialData: () => {
      if (props.initialData) {
        // Verify that this initial data was created for this specific query key
        const initialKey = serverSideProps.initialKey;
        const currentKey = JSON.stringify(["productListing", category.fields.categoryId, props.setId, sort]);

        if (initialKey === currentKey) {
          return { pages: [JSON.parse(props.initialData)], pageParams: [1] };
        }
      }

      return undefined;
    },
  });

  const ssrTotal = props.serverState
    ? props.serverState.initialResults[algoliaIndexId].results?.[0].nbHits
    : query?.data?.pages[0].Total && query.data?.pages[0].Total;

  const [liveTotal, setLiveTotal] = useState<number | undefined>(undefined);
  const totalNumberProducts = liveTotal ?? ssrTotal;

  const [showMore, setShowMore] = useState(false);

  const headerCssVariables = useMemo(() => {
    if (!props.plpCustomHeader) {
      return {};
    }

    return {
      "--plp-custom-header-background-color-mobile": props.plpCustomHeader.backgroundColorMobile,
      "--plp-custom-header-title-size-mobile": props.plpCustomHeader.titleSizeMobile / 16 + "rem",
      "--plp-custom-header-description-size-mobile": props.plpCustomHeader.descriptionSizeMobile / 16 + "rem",
    } as CSSProperties;
  }, [props.plpCustomHeader]);

  const color1 = props.plpCustomHeader?.shadowBackgroundColorFirst || props.backgroundColors?.[0];
  const color2 = props.plpCustomHeader?.shadowBackgroundColorSecond || props.backgroundColors?.[1] || color1;

  const renderProductListing = (forPaginationMode: boolean) => {
    const mode = forPaginationMode ? isPaginationMode : false;

    if (props.serverState && props.serverUrl) {
      return (
        <ProductGridSkeleton
          serverUrl={props.serverUrl}
          serverState={props.serverState}
          algoliaIndexId={algoliaIndexId}
          algoliaUserToken={props.algoliaUserToken}
          categoryId={category.fields.categoryId}
          components={props.components}
          listScope={props.listScope}
          isPaginationMode={mode}
          products={props.products}
          pageNumber={props.pagination?.currentPage}
          onTotalChange={setLiveTotal}
        />
      );
    }

    return (
      <EvaProductListing
        components={props.components}
        listScope={props.listScope}
        query={query}
        isPaginationMode={mode}
        staticProducts={props.products}
      />
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <ProductGridCustomBackground
        color1={color1}
        color2={color2}
        changeHeaderLayout={props.changeHeaderLayout}
        defaultDesktopBackground
        hasBannerHeader={!!category.fields.bannerHeader}
        type="desktop"
      />
      {category.fields.bannerHeader ? (
        <div className={styles.bannerHeaderWrapper}>
          <BreakpointAwareAsset entry={category.fields.bannerHeader} />
          <div className={styles.textWrapper}>
            {category.fields.previousPageLink ? <EditorialLink entry={category.fields.previousPageLink} /> : null}
            <h1>
              <HighlightedText text={category.fields?.formattedName ?? category.fields.name} />
            </h1>
            {category.fields.subtitle ? (
              <div className={styles.subtitle}>
                <EscapeNewLine text={category.fields.subtitle} />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <Container>
        <Grid className={styles.grid}>
          <div
            className={classNames(
              styles.headingSection,
              props.changeHeaderLayout ? styles.customHeadingSection : undefined
            )}
            style={headerCssVariables}
          >
            <ProductGridCustomBackground
              color1={color1}
              color2={color2}
              changeHeaderLayout={props.changeHeaderLayout}
              hasBannerHeader={!!category.fields.bannerHeader}
              type="mobile"
            />
            <Container
              className={classNames(
                styles.headingColumn,
                props.changeHeaderLayout ? styles.customHeadingColumn : undefined
              )}
            >
              {props.breadcrumbs && props.breadcrumbs.length > 0 ? (
                <Breadcrumbs breadcrumbs={props.breadcrumbs} />
              ) : null}
              {!category.fields.bannerHeader ? (
                <>
                  {category.fields.previousPageLink ? <EditorialLink entry={category.fields.previousPageLink} /> : null}
                  <h1>
                    <HighlightedText text={category.fields?.formattedName ?? category.fields.name} />{" "}
                  </h1>
                </>
              ) : null}
              {category.fields.subtitle && !category.fields.bannerHeader ? (
                <div className={styles.subtitle}>
                  <EscapeNewLine text={category.fields.subtitle} />
                </div>
              ) : null}
              {category.fields.description || category.fields.readMoreDescription ? (
                <div>
                  {category.fields.description ? (
                    <p className={styles.description}>
                      <EscapeNewLine text={category.fields.description} />
                    </p>
                  ) : null}
                  {category.fields.readMoreDescription ? (
                    <p
                      className={
                        showMore ? classNames(styles.showMoreTextOpen, styles.showMoreText) : styles.showMoreText
                      }
                    >
                      <span className={styles.innerShowMoreText}>
                        <EscapeNewLine text={category.fields.readMoreDescription} />
                      </span>
                    </p>
                  ) : null}

                  {category.fields.readMoreDescription ? (
                    <button onClick={() => setShowMore(!showMore)} className={styles.showMoreButton}>
                      {showMore ? t("generic.read_less") : t("generic.read_more")}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <ProductNavigationItemsCurrentPath
                categoryLinks={props.categoryLinks}
                listLinks={category.fields.listLinks?.filter(isActiveEntry)}
                changeHeaderLayout={props.changeHeaderLayout}
              />
              <NumberProducts total={totalNumberProducts} type="desktop" />
            </Container>
          </div>
          <NumberProducts total={totalNumberProducts} type="mobile" />
          {props.pagination ? (
            <>
              {/* Keep same component instance mounted — isPaginationMode prop controls the mode */}
              {renderProductListing(true)}

              <Pagination
                currentPage={props.pagination.currentPage}
                totalPages={props.pagination.totalPages}
                baseUrl={props.pagination.baseUrl}
                preserveFilters={props.pagination.preserveFilters}
                visible={isPaginationMode}
              />
            </>
          ) : (
            renderProductListing(false)
          )}
        </Grid>
      </Container>
      {componentsAfterListing && (
        <div className={styles.containerComponentsAfterListing}>
          {componentsAfterListing.map((entry) => (
            <EditorialEntry key={entry.sys.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
